import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Brain, Mail } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, Input } from '../../components/index.js';
import { useAuth } from '../../lib/auth-context.js';

export function Signup() {
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await signUp(email, password, name.trim() || undefined);
      navigate(from, { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      navigate(from, { replace: true });
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <header className="mb-8 text-center">
          <span className="inline-flex size-12 items-center justify-center rounded-xl bg-accent-muted text-accent-hover mb-4">
            <Brain className="size-6" aria-hidden />
          </span>
          <h1 className="text-3xl font-bold text-text-primary">Create your TAS Portal account</h1>
          <p className="mt-2 text-base text-text-secondary">Access to the portal is by invitation.</p>
        </header>
        <Card>
          <CardHeader title="Sign up" />
          <CardBody>
            <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
              <Input
                label="Full name"
                type="text"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
              />
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                leftAdornment={<Mail className="size-4" aria-hidden />}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                helperText="Minimum 8 characters."
              />
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <Button type="submit" loading={loading} fullWidth>
                Create account
              </Button>
            </form>
            <div className="my-5 flex items-center gap-3">
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
              <span className="text-sm text-text-secondary">or</span>
              <span className="h-px flex-1 bg-border-subtle" aria-hidden />
            </div>
            <Button type="button" variant="secondary" fullWidth onClick={onGoogle} loading={googleLoading}>
              Continue with Google
            </Button>
          </CardBody>
        </Card>
        <p className="mt-6 text-center text-sm text-text-secondary">
          Already have an account?{' '}
          <Link to="/login" className="text-accent-hover hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

function messageFor(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code: string }).code;
    switch (code) {
      case 'auth/email-already-in-use':
        return 'An account with that email already exists. Try signing in.';
      case 'auth/invalid-email':
        return 'That email address is not valid.';
      case 'auth/weak-password':
        return 'That password is too weak. Use at least 8 characters.';
      case 'auth/network-request-failed':
        return 'Network error. Check your connection and try again.';
      default:
        return code.replace(/^auth\//, '').replace(/-/g, ' ');
    }
  }
  return err instanceof Error ? err.message : 'Sign-up failed.';
}
